import type { PlayerListItem } from "../../shared/types/contracts";

export type LineupContainer = "lineup" | "bench";

export interface LineupPartition {
  lineup: string[];
  bench: string[];
}

export function buildLineupPartition(
  players: PlayerListItem[],
  lineupSlots: Array<string | null>,
  reorderOnly: boolean,
): LineupPartition {
  const lineup = lineupSlots.filter((id): id is string => Boolean(id));
  const lineupIds = new Set(lineup);
  return {
    lineup,
    bench: reorderOnly
      ? []
      : players
          .filter((player) => !lineupIds.has(player.id))
          .map((player) => player.id),
  };
}

export function inactivePlayerIds(
  players: PlayerListItem[],
  activeIds: string[],
): Set<string> {
  const active = new Set(activeIds);
  return new Set(
    players
      .filter((player) => !active.has(player.id))
      .map((player) => player.id),
  );
}

export function findLineupContainer(
  partition: LineupPartition,
  id: string,
): LineupContainer | null {
  if (id === "lineup-dropzone") return "lineup";
  if (id === "bench-dropzone") return "bench";
  if (partition.lineup.includes(id)) return "lineup";
  if (partition.bench.includes(id)) return "bench";
  return null;
}

export function moveLineupItem(
  partition: LineupPartition,
  playerId: string,
  destination: LineupContainer,
  targetId: string | null,
): LineupPartition {
  const next = {
    lineup: partition.lineup.filter((id) => id !== playerId),
    bench: partition.bench.filter((id) => id !== playerId),
  };
  const destinationItems = next[destination];
  const targetIndex = targetId ? destinationItems.indexOf(targetId) : -1;
  const insertAt = targetIndex >= 0 ? targetIndex : destinationItems.length;
  next[destination] = [
    ...destinationItems.slice(0, insertAt),
    playerId,
    ...destinationItems.slice(insertAt),
  ];
  return next;
}

export function reorderLineupItem(
  partition: LineupPartition,
  container: LineupContainer,
  playerId: string,
  targetId: string | null,
): LineupPartition {
  if (!targetId) return partition;
  const items = partition[container];
  const from = items.indexOf(playerId);
  const to = items.indexOf(targetId);
  if (from < 0 || to < 0 || from === to) return partition;
  const reordered = [...items];
  const [moved] = reordered.splice(from, 1);
  if (!moved) return partition;
  reordered.splice(to, 0, moved);
  return { ...partition, [container]: reordered };
}
